/**
 * Утилиты для работы с HTTP заголовками
 */

/**
 * Получает значение заголовка X-Device-Type
 * @returns Строка с информацией об устройстве, ОС и версии
 */
export function getXDeviceType(): string {
  const userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''

  // Инициализация значений
  let deviceType: string = 'Desktop'
  let os: string = 'Unknown OS'
  let osVersion: string = 'Unknown Version'

  // Определяем тип устройства
  if (/Mobi|Android/i.test(userAgent)) {
    deviceType = 'Mobile'
  }

  // Определяем ОС и версию
  if (/Windows NT (\d+\.\d+)/.test(userAgent)) {
    os = 'Windows'
    osVersion = userAgent.match(/Windows NT (\d+\.\d+)/)?.[1] || 'Unknown Version'
  } else if (/Mac OS X (\d+[\._]\d+)/.test(userAgent)) {
    os = 'Mac OS'
    osVersion = userAgent.match(/Mac OS X (\d+[\._]\d+)/)?.[1]?.replace('_', '.') || 'Unknown Version'
  } else if (/Linux/.test(userAgent)) {
    os = 'Linux'
    osVersion = 'Unknown Version'
  } else if (/iPhone|iPad|iPod/.test(userAgent)) {
    os = 'iOS'
    osVersion = userAgent.match(/OS (\d+[\._]\d+)/)?.[1]?.replace('_', '.') || 'Unknown Version'
  } else if (/Android/.test(userAgent)) {
    os = 'Android'
    osVersion = userAgent.match(/Android (\d+[\.\d]+)/)?.[1] || 'Unknown Version'
  }

  // Формируем строку в нужном формате
  return `Device: ${deviceType}; Os: ${os}; Version: ${osVersion}`
}

/**
 * Получает значение заголовка X-Timestamp
 * @returns Строка с временной меткой, GMT offset и timezone
 */
export function getXTimestamp(): string {
  const date = new Date()
  const gmtOffset = `GMT${date.getTimezoneOffset() < 0 ? '+' : '-'}${Math.abs(date.getTimezoneOffset() / 60)}`
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return `${date.toISOString()}; ${gmtOffset}; ${timeZone}`
}

/**
 * Получает значение заголовка X-User-Agent
 * @returns Строка с информацией о браузере, версии, поддержке cookies и языке
 */
export function getXUserAgent(): string {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const browserMatch = userAgent.match(/(Chrome|Firefox|Safari|Opera|Edge)[\/\s](\d+\.\d+\.\d+)/)

  // Получаем язык пользователя
  const lang = typeof navigator !== 'undefined' ? (navigator.language || navigator.language || 'Unknown language') : 'Unknown language'

  // Присваиваем значения в переменные, используя конструкцию || для задания значения по умолчанию
  const browser = (browserMatch && browserMatch[1]) || 'Unknown'
  const version = (browserMatch && browserMatch[2]) || 'Unknown'
  const cookiesEnabled = typeof navigator !== 'undefined' ? (navigator.cookieEnabled || false) : false

  // Формируем строку в нужном формате
  return `Agent: ${browser} ${version}; Cookies: ${cookiesEnabled}; Lang: ${lang}`
}

/**
 * Получает значение заголовка X-Location из window.location.href
 * @returns URL текущей страницы или пустую строку, если window недоступен
 */
export function getXLocation(): string {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.href
  }
  return ''
}

/**
 * Получает информацию о языке пользователя
 * @returns Объект с информацией о языке (primary, all, code, region)
 */
export function getXLanguage(): {
  primary: string
  all: string[]
  code: string
  region: string
} {
  // Отримуємо основний мову браузера або встановлюємо англійську за замовчуванням
  const primaryLanguage =
    typeof navigator !== 'undefined'
      ? navigator.language || (navigator as any)?.userLanguage || 'en'
      : 'en'

  // Отримуємо масив бажаних мов (якщо потрібно)
  const languages = typeof navigator !== 'undefined' ? [...(navigator.languages || [primaryLanguage])] : [primaryLanguage]

  // Повертаємо об'єкт з інформацією про мову
  return {
    primary: primaryLanguage, // наприклад, 'ru-RU', 'en-US'
    all: languages, // масив всіх бажаних мов
    code: primaryLanguage.split('-')[0], // код мови, наприклад 'ru', 'en'
    region: primaryLanguage.split('-')[1] || '', // регіон, наприклад 'RU', 'US'
  }
}

/**
 * Проверяет валидность кода страны
 * @param value - Значение заголовка
 * @returns true если валидный ISO код страны (2 символа)
 */
function isValidCountryCode(value: unknown): value is string {
  return typeof value === 'string' && value.length === 2
}

/**
 * Нормализует код страны (приводит к верхнему регистру)
 * @param code - Код страны
 * @returns Нормализованный код или null
 */
function normalizeCountryCode(code: unknown): string | null {
  if (!isValidCountryCode(code)) {
    return null
  }
  return code.toUpperCase()
}

/**
 * Получает значение заголовка по ключам (case-insensitive)
 * @param headers - Заголовки запроса
 * @param keys - Массив возможных ключей заголовка
 * @returns Значение заголовка или undefined
 */
function getHeaderValue(
  headers: Record<string, string | string[] | undefined>,
  keys: string[]
): string | string[] | undefined {
  for (const key of keys) {
    const value = headers[key]
    if (value !== undefined) {
      return value
    }
  }
  return undefined
}

/**
 * Проверяет один CDN заголовок
 * @param headers - Заголовки запроса
 * @param keys - Массив возможных ключей заголовка
 * @param headerName - Название заголовка для логов
 * @param logPrefix - Префикс для логов (опционально)
 * @returns ISO код страны или null
 */
function checkSingleCdnHeader(
  headers: Record<string, string | string[] | undefined>,
  keys: string[],
  headerName: string,
  logPrefix?: string
): string | null {
  const headerValue = getHeaderValue(headers, keys)
  const country = normalizeCountryCode(headerValue)
  
  if (country) {
    if (logPrefix && import.meta.dev) {
      console.log(`${logPrefix} ✓ Найден заголовок ${headerName}: ${country}`)
    }
    return country
  }
  
  return null
}

/**
 * Внутренняя функция для проверки CDN заголовков
 * Проверяет все возможные CDN заголовки и возвращает найденный код страны
 * @param headers - Заголовки запроса
 * @param logPrefix - Префикс для логов (опционально)
 * @returns ISO код страны или null если не найден
 */
function checkCdnHeaders(
  headers: Record<string, string | string[] | undefined>,
  logPrefix?: string
): string | null {
  // Cloudflare
  const cfCountry = checkSingleCdnHeader(
    headers,
    ['cf-ipcountry', 'CF-IPCountry'],
    'CF-IPCountry',
    logPrefix
  )
  if (cfCountry) return cfCountry

  // Vercel
  const vercelCountry = checkSingleCdnHeader(
    headers,
    ['x-vercel-ip-country', 'X-Vercel-IP-Country'],
    'X-Vercel-IP-Country',
    logPrefix
  )
  if (vercelCountry) return vercelCountry

  // AWS CloudFront
  const cloudfrontCountry = checkSingleCdnHeader(
    headers,
    ['cloudfront-viewer-country', 'CloudFront-Viewer-Country'],
    'CloudFront-Viewer-Country',
    logPrefix
  )
  if (cloudfrontCountry) return cloudfrontCountry

  // Другие CDN/прокси заголовки
  const xCountry = checkSingleCdnHeader(
    headers,
    ['x-country', 'X-Country'],
    'X-Country',
    logPrefix
  )
  if (xCountry) return xCountry

  return null
}

/**
 * Проверяет наличие CDN заголовков
 * @param headers - Опциональные заголовки запроса (для SSR)
 * @returns true если найден CDN заголовок, false если нет
 */
export function hasCdnHeaders(headers?: Record<string, string | string[] | undefined>): boolean {
  if (import.meta.server && headers) {
    return checkCdnHeaders(headers) !== null
  }
  return false
}

/**
 * Получает ISO код страны клиента только из CDN заголовков (без fallback на язык)
 * @param headers - Опциональные заголовки запроса (для SSR)
 * @returns ISO код страны или null если CDN заголовки не найдены
 */
export function getCountryFromCdn(headers?: Record<string, string | string[] | undefined>): string | null {
  const isDev = import.meta.dev
  const logPrefix = '[getCountryFromCdn]'

  // На сервере проверяем заголовки CDN/прокси
  if (import.meta.server && headers) {
    if (isDev) {
      console.log(`${logPrefix} Проверка CDN заголовков на сервере`)
    }

    const country = checkCdnHeaders(headers, logPrefix)
    
    if (!country && isDev) {
      console.log(`${logPrefix} ✗ CDN заголовки не найдены`)
    }

    return country
  }

  return null
}

/**
 * Получает ISO код страны клиента по заголовкам CDN/прокси
 * Приоритет: CDN заголовки → регион из языка → null
 * @param headers - Опциональные заголовки запроса (для SSR)
 * @returns ISO код страны (например, 'UA', 'US', 'GB') или null
 */
export function getXCountry(headers?: Record<string, string | string[] | undefined>): string | null {
  const isDev = import.meta.dev
  const logPrefix = '[getXCountry]'

  // Сначала проверяем CDN заголовки
  const cdnCountry = getCountryFromCdn(headers)
  if (cdnCountry) {
    return cdnCountry
  }

  // Fallback: используем регион из языка браузера
  const languageInfo = getXLanguage()
  if (languageInfo.region && languageInfo.region.length === 2) {
    const country = languageInfo.region.toUpperCase()
    if (isDev) {
      console.log(`${logPrefix} → Fallback: страна из языка браузера (${languageInfo.primary}): ${country}`)
    }
    return country
  }

  if (isDev) {
    console.log(`${logPrefix} ✗ Страна не определена (нет CDN заголовков и региона в языке)`)
  }

  return null
}

/**
 * Получает ISO код страны клиента через API запрос
 * Используется как fallback, если CDN заголовки недоступны
 * @returns Promise с ISO кодом страны или null
 */
export async function getCountryFromApi(): Promise<string | null> {
  const isDev = import.meta.dev
  const logPrefix = '[getCountryFromApi]'

  // Только на клиенте (браузер)
  if (import.meta.server) {
    if (isDev) {
      console.log(`${logPrefix} API запрос доступен только на клиенте`)
    }
    return null
  }

  try {
    if (isDev) {
      console.log(`${logPrefix} Запрос страны через API...`)
    }

    // Используем бесплатный API ipapi.co (без ключа, лимит 1000 запросов/день)
    // Можно заменить на другой сервис или внутренний API
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Таймаут 3 секунды
      signal: AbortSignal.timeout(3000),
    })

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`)
    }

    const data = await response.json()
    const countryCode = data.country_code || data.country || null

    if (countryCode && typeof countryCode === 'string' && countryCode.length === 2) {
      const country = countryCode.toUpperCase()
      if (isDev) {
        console.log(`${logPrefix} ✓ Страна получена через API: ${country}`)
      }
      return country
    }

    if (isDev) {
      console.warn(`${logPrefix} ✗ API вернул невалидный код страны:`, countryCode)
    }
    return null
  } catch (error) {
    if (isDev) {
      console.warn(`${logPrefix} ✗ Ошибка при запросе страны через API:`, error)
    }
    return null
  }
}
